import React, { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

const STATUS = gql`query PersonalReminderEmailStatus($businessId: ID!) {
  personalReminderEmailStatus(businessId: $businessId) { email pendingEmail enabled }
}`;
const REQUEST = gql`mutation RequestPersonalReminderEmail($businessId: ID!, $email: String!) {
  requestPersonalReminderEmail(businessId: $businessId, email: $email) { email pendingEmail enabled }
}`;
const VERIFY = gql`mutation VerifyPersonalReminderEmail($businessId: ID!, $code: String!) {
  verifyPersonalReminderEmail(businessId: $businessId, code: $code) { email pendingEmail enabled }
}`;
const DISABLE = gql`mutation DisablePersonalReminderEmail($businessId: ID!) {
  disablePersonalReminderEmail(businessId: $businessId) { email pendingEmail enabled }
}`;

export default function PersonalReminderEmail({ businessId }) {
  const { data, loading, error: loadError, refetch } = useQuery(STATUS, { variables: { businessId }, fetchPolicy: 'network-only' });
  const [request] = useMutation(REQUEST);
  const [verify] = useMutation(VERIFY);
  const [disable] = useMutation(DISABLE);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const status = data?.personalReminderEmailStatus;
  const act = async (mutation, values) => {
    setBusy(true); setError('');
    try {
      await mutation({ variables: { businessId, ...values } });
      await refetch(); setEditing(false); setCode('');
    } catch (e) {
      setError(e.message || 'Unable to update reminder email.');
      // The server pauses reminders before sending a verification email.
      // Refresh even when delivery fails so the displayed state stays accurate.
      try { await refetch(); } catch { /* The query displays its own load error. */ }
    }
    finally { setBusy(false); }
  };
  if (loading && !status) return <Typography variant="body2" sx={{ mb: 2 }}>Loading reminder email…</Typography>;
  if (loadError) return <Alert severity="error" sx={{ mb: 2 }}>Unable to load reminder email. <Button onClick={() => refetch()}>Retry</Button></Alert>;
  return <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
    <Typography variant="subtitle2">Personal reminder email</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {status?.enabled ? `Reminders go to ${status.email}` : 'Email reminders are off. Verify an address to enable them.'}
    </Typography>
    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
    {status?.enabled && !editing ? <Stack direction="row" spacing={1}>
      <Button size="small" disabled={busy} onClick={() => { setEmail(''); setEditing(true); }}>Change email</Button>
      <Button size="small" disabled={busy} onClick={() => act(disable, {})}>Turn off email reminders</Button>
    </Stack> : <>
      {status?.pendingEmail && !editing ? <>
        <Typography variant="body2" sx={{ mb: 1 }}>Enter the code sent to {status.pendingEmail}.</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField label="Verification code" size="small" value={code} disabled={busy} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }} />
          <Button variant="outlined" disabled={busy || code.length !== 6} onClick={() => act(verify, { code })}>Verify & enable</Button>
          <Button disabled={busy} onClick={() => act(request, { email: status.pendingEmail })}>Resend code</Button>
        </Stack>
        <Button size="small" disabled={busy} onClick={() => { setEmail(''); setEditing(true); }}>Use another email</Button>
        <Button size="small" disabled={busy} onClick={() => act(disable, {})}>Cancel verification</Button>
      </> : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
        <TextField label="Reminder email" type="email" size="small" value={email} disabled={busy} onChange={e => setEmail(e.target.value)} helperText="Choose a personal address or your login email." sx={{ flex: 1 }} />
        <Button variant="outlined" disabled={busy || !email.trim()} onClick={() => act(request, { email })}>Send verification code</Button>
        {editing && <Button disabled={busy} onClick={() => setEditing(false)}>Cancel</Button>}
      </Stack>}
    </>}
  </Box>;
}
